import React, { useEffect } from 'react';
import { Steps, Spin, Tag, Typography, Tooltip, Empty } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import useModuleGroupStore from '../../stores/useModuleGroupStore';

const { Text, Paragraph } = Typography;

const STATUS_COLORS = { active: 'blue', completed: 'green', dropped: 'red' };

function bfsModuleOrder(modules, startModuleId) {
    const adjMap = {};
    modules.forEach((m) => {
        const connGotos = (m.connection || []).map((c) => c.goto);
        const triggerGotos = (m.triggers || [])
            .filter((t) => !t.deleted_at && t.goto_module_id)
            .map((t) => t.goto_module_id);
        adjMap[m.id] = connGotos.concat(triggerGotos);
    });

    const visited = new Set();
    const order = [];
    const queue = [startModuleId];
    while (queue.length) {
        const current = queue.shift();
        if (!current || visited.has(current)) continue;
        visited.add(current);
        order.push(current);
        (adjMap[current] || []).forEach((nextId) => {
            if (!visited.has(nextId)) queue.push(nextId);
        });
    }
    modules.forEach((m) => {
        if (!visited.has(m.id)) order.push(m.id);
    });
    return order;
}

export default function ContactFlowStepper({ enrollment }) {
    const { moduleGroup, modules, loading, fetchModuleGroup } = useModuleGroupStore();

    useEffect(() => {
        if (enrollment?.modulegroup_id) {
            if (!moduleGroup || moduleGroup.id !== enrollment.modulegroup_id) {
                fetchModuleGroup(enrollment.modulegroup_id);
            }
        }
    }, [enrollment?.modulegroup_id]);

    if (!enrollment) return null;

    if (loading || !modules || modules.length === 0 || !moduleGroup || moduleGroup.id !== enrollment.modulegroup_id) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin /></div>;
    }

    const startModuleId = moduleGroup.module_id || modules[0]?.id;
    const orderedIds = startModuleId ? bfsModuleOrder(modules, startModuleId) : modules.map((m) => m.id);
    const moduleMap = {};
    modules.forEach((m) => { moduleMap[m.id] = m; });

    const currentIdx = orderedIds.indexOf(enrollment.current_module_id);
    const isDropped = enrollment.status === 'dropped';
    const isCompleted = enrollment.status === 'completed';

    const items = orderedIds.map((id, idx) => {
        const mod = moduleMap[id];
        const title = mod?.title || id.slice(0, 8);
        const responseText = mod?.response || '';
        const isCurrent = idx === currentIdx;

        let status = 'wait';
        if (isCompleted) {
            status = 'finish';
        } else if (isDropped) {
            status = isCurrent ? 'error' : (idx < currentIdx ? 'finish' : 'wait');
        } else if (idx < currentIdx) {
            status = 'finish';
        } else if (isCurrent) {
            status = 'process';
        }

        return {
            title: (
                <Tooltip title={responseText ? <span style={{ whiteSpace: 'pre-wrap' }}>{responseText}</span> : null}>
                    <span style={{ fontSize: 12, fontWeight: isCurrent ? 600 : 400 }}>{title}</span>
                </Tooltip>
            ),
            status,
            description: isCurrent ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {isDropped ? 'Stopped here' : isCompleted ? 'Completed' : 'Currently here'}
                </Text>
            ) : null,
        };
    });

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Tag color={STATUS_COLORS[enrollment.status] || 'default'} style={{ margin: 0 }}>
                    {enrollment.status}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Enrolled {moment(enrollment.created_at).fromNow()} · {orderedIds.length} {orderedIds.length === 1 ? 'step' : 'steps'}
                </Text>
                {enrollment.bot_mobile && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        · Sent from {enrollment.bot_name || enrollment.bot_mobile}
                    </Text>
                )}
                {enrollment.enrolled_by_name && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        · By {enrollment.enrolled_by_name}
                    </Text>
                )}
            </div>

            {/* Deleted-flow banner */}
            {moduleGroup.deleted_at && (
                <div style={{
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: 6,
                    padding: '8px 12px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}>
                    <WarningOutlined style={{ color: '#d48806' }} />
                    <Text style={{ fontSize: 12 }}>
                        This flow was deleted {moment(moduleGroup.deleted_at).fromNow()}. Progress is shown for historical reference.
                    </Text>
                </div>
            )}

            {/* Drop reason banner */}
            {isDropped && enrollment.drop_reason && (
                <div style={{
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    borderRadius: 6,
                    padding: '8px 12px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}>
                    <StopOutlined style={{ color: '#cf1322' }} />
                    <Text style={{ fontSize: 12 }}>{enrollment.drop_reason}</Text>
                </div>
            )}

            {/* Completed banner */}
            {isCompleted && (
                <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 6,
                    padding: '8px 12px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}>
                    <CheckCircleFilled style={{ color: '#52c41a' }} />
                    <Text style={{ fontSize: 12 }}>Contact reached the end of this flow.</Text>
                </div>
            )}

            {/* The stepper itself */}
            {orderedIds.length === 0 ? (
                <Empty description="This flow has no modules" />
            ) : (
                <Steps
                    direction="vertical"
                    size="small"
                    current={currentIdx >= 0 ? currentIdx : undefined}
                    items={items}
                />
            )}

            {/* Module response preview for current */}
            {currentIdx >= 0 && enrollment.status === 'active' && moduleMap[enrollment.current_module_id]?.response && (
                <div style={{
                    marginTop: 16,
                    padding: '12px 14px',
                    background: '#f5f5f5',
                    borderRadius: 6,
                    borderLeft: '3px solid #1677ff',
                }}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        <ClockCircleOutlined /> Awaiting reply on this message:
                    </Text>
                    <Paragraph style={{ marginBottom: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                        {moduleMap[enrollment.current_module_id].response}
                    </Paragraph>
                </div>
            )}
        </div>
    );
}
